import { onMounted, watch } from 'vue'
import { isAxiosError } from 'axios'
import {
  addRumAction,
  addRumError,
  cleanContext,
  isDatadogRumInitialized,
} from '@/Utils/DatadogRum.js'

/**
 * Flag to add on the axios request config to skip the error tracking interceptor in BaseApi
 */
export const RUM_SKIP_INTERCEPTOR_ERROR_TRACKING = 'RUM_SKIP_INTERCEPTOR_ERROR_TRACKING'

/**
 * Composable to track custom actions and form validation errors in Datadog RUM.
 *
 * @param {string} actionName - The name of the custom action to track
 * @param {Record<string, unknown>} context - The context of the custom action to track (no PII)
 * @property {import('@inertiajs/vue3').Form} [context.form] - Inertia useForm() instance; when present, validation errors are tracked
 */
export function useRumCustomAction(actionName, context = {}) {
  if (!isDatadogRumInitialized()) {
    return
  }

  const { form, ...restContext } = context

  onMounted(() => {
    addRumAction(actionName, cleanContext(restContext))
  })

  // If the context has a form, watch for the form errors and send a RUM action to track the validation errors
  if (form) {
    useRumWatchFormErrors(form, actionName)
  }
}

/**
 * Watch the form errors and send a RUM action to track the validation errors
 *
 * @param {import('@inertiajs/vue3').Form} form - The form to watch
 * @param {string} actionName - The name of the custom action to track
 */
export function useRumWatchFormErrors(form, actionName) {
  if (!isDatadogRumInitialized()) {
    return
  }

  watch(
    // Watch a single string that only changes when the error set changes
    () => JSON.stringify(form.errors ?? {}),
    (errorsJson, previousErrorsJson) => {
      try {
        //  extra guard, avoid running the logic when the serialized errors are unchanged (e.g. same errors after a re-submit)
        if (errorsJson === previousErrorsJson) {
          return
        }

        const errors = JSON.parse(errorsJson)
        const fields = Object.keys(errors)

        // When there are no errors, return early without sending a RUM action
        if (fields.length === 0) {
          return
        }

        const error = new Error('Form validation errors')

        addRumError(error, {
          custom_action: actionName,
          error_count: fields.length,
          fields,
        })
      } catch (error) {
        console.error('Error watching form errors', error)
      }
    }
  )
}

/**
 * @param {import('axios').AxiosError} axiosError - The axios error
 *
 * @returns {Record<string, unknown>} The context of the error
 */
function parseContextFromAxiosError(axiosError) {
  const data = axiosError.response?.data?.data ?? {}
  const errorFields = Object.keys(data)
  const errorMessages = Object.values(data).flat()
  const message = `Error ${axiosError.response?.status} ${axiosError.response?.config?.method?.toUpperCase()} ${getUrlPathGroup(axiosError.response?.config?.url)}`

  return {
    message,
    status: axiosError.response?.status,
    error_count: errorFields.length,
    fields: errorFields,
    messages: errorMessages,
  }
}

/**
 * Parse the context from the response error
 *
 * @param {Object} responseError - The response error
 * @returns {Record<string, unknown>} The context of the error
 */
function parseContextFromResponseError(responseError) {
  switch (true) {
    case isAxiosError(responseError):
      return parseContextFromAxiosError(responseError)
    default:
      return { message: 'Response error' }
  }
}

/**
 * Add a RUM error to the Datadog RUM session
 *
 * @param {Object} responseError - The response error
 * @param {Record<string, unknown>} context - The context of the error
 */
export function addRumResponseError(responseError, context = {}) {
  try {
    if (!isDatadogRumInitialized()) {
      return
    }

    const { message, ...restContext } = parseContextFromResponseError(responseError)
    const error = new Error(message)

    addRumError(error, {
      ...context,
      ...restContext,
    })
  } catch (error) {
    console.error('Error adding RUM Response error', error)
  }
}

/**
 * Get the URL path group generated for similar URLs. For example, dashboard/1234 and /dashboard/9a become /dashboard/?.
 * A path segment that contains at least one number is considered a variable ID.
 *
 * @param {string} url
 * @returns {string}
 */
export function getUrlPathGroup(url) {
  if (!url || typeof url !== 'string') {
    return '/'
  }

  let pathname = url.trim()

  try {
    pathname = new URL(pathname).pathname
  } catch {
    pathname = pathname.split(/[?#]/)[0]
  }

  const trimmed = pathname.replace(/\/+$/, '') || '/'

  if (trimmed === '/') {
    return '/'
  }

  const segments = trimmed.slice(1).split('/')
  const apiVersion = segments.shift()
  const grouped = segments.map((segment) => (/[0-9]/.test(segment) ? '?' : segment))

  return `/${apiVersion}/${grouped.join('/')}`
}
